import React, { useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Select from '@atlaskit/select/default';
import type {
	DropdownIndicatorProps,
	InputActionMeta,
	MenuProps,
	OptionProps,
	OptionType,
	SelectInstance,
} from '@atlaskit/select/types';

import {
	type BaseSelectProps,
	type ControlProps,
	MetaActions,
	type SelectOption,
	type SingleOption,
} from '../../../common/types';
import Control from './control';
import DropdownIndicator from './dropdown-indicator';
import Menu from './menu';
import messages from './messages';
import Option from './option';

export const selectComponents: {
	Control: ({ children, innerProps, innerRef }: ControlProps) => React.JSX.Element;
	DropdownIndicator: (props: DropdownIndicatorProps<SingleOption, boolean>) => React.JSX.Element;
	LoadingIndicator: null;
	Menu: ({ innerRef, innerProps, children }: MenuProps<SingleOption, boolean>) => React.JSX.Element;
	Option: (props: OptionProps<OptionType, boolean>) => React.JSX.Element;
} = {
	Control,
	DropdownIndicator,
	LoadingIndicator: null,
	Menu,
	Option,
};

const onIsOptionDisabled = (option: SelectOption) => ('error' in option ? !!option.error : false);
export const LocalBaseSelect: React.ForwardRefExoticComponent<
	Omit<BaseSelectProps, 'ref'> & React.RefAttributes<SelectInstance>
> = React.forwardRef<SelectInstance, BaseSelectProps>(
	({ onFilterOption, placeholder, ...restProps }, ref) => {
		const intl = useIntl();
		const {
			ariaLabel: ariaLabelProp,
			isMulti = true,
			onChange: onChangeProp,
			onInputChange: onInputChangeProp,
			inputValue: inputValueProp,
			value,
		} = restProps;
		const effectiveIsMulti = isMulti;

		const [inputValue, setInputValue] = useState<string>(inputValueProp || '');

		const onInputChange = useCallback(
			(newValue: string, actionMeta: InputActionMeta) => {
				/**
				 * Do not clear the filter input value when an input is selected or blurred.
				 * NOTE: We are also returning when 'menu-close' event are triggered,
				 * which happens when the input is clicked for some reason, but not when we click outside of the dropdown
				 * */
				if (
					[MetaActions.setValue, MetaActions.inputBlur, MetaActions.menuClose].includes(
						actionMeta.action,
					) &&
					!newValue.length
				) {
					return;
				}

				onInputChangeProp?.(newValue, actionMeta);
				setInputValue(newValue);
			},
			[onInputChangeProp],
		);

		const onChange = useCallback(
			(newValue: unknown, actionMeta: unknown) => {
				// Keep this wrapper unconditional for both modes so hook creation stays stable.
				// In multiselect mode `newValue` is already an array, making this a no-op; in
				// single-select mode Atlaskit returns one option (or null), which we normalize
				// back to the array contract expected by the paginated-filter stack.
				const normalizedNewValue = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];
				onChangeProp?.(normalizedNewValue as any, actionMeta as any);
			},
			[onChangeProp],
		);

		const normalizedValue = effectiveIsMulti
			? value
			: Array.isArray(value)
				? (value[0] ?? null)
				: (value ?? null);

		return (
			<Select
				{...(restProps as any)} // Typescript fix: SelectOption is not compatible with SingleOption from Select
				ref={ref}
				aria-label={ariaLabelProp || intl.formatMessage(messages.searchPlaceholder)}
				autoFocus
				backspaceRemovesValue={false}
				blurInputOnSelect={false}
				closeMenuOnSelect={!effectiveIsMulti}
				controlShouldRenderValue={false}
				hideSelectedOptions={false}
				isClearable={false}
				isMulti={effectiveIsMulti}
				inputValue={inputValue}
				menuIsOpen
				menuShouldScrollIntoView={false}
				tabSelectsValue={false}
				noOptionsMessage={() => intl.formatMessage(messages.noOptionsMessage)}
				// Typescript fix: normalized onChange signature (single/multi) is not directly assignable to Select's onChange type
				onChange={onChange as any}
				onInputChange={onInputChange}
				isOptionDisabled={onIsOptionDisabled as any} // Typescript fix: SelectOption is not compatible with SingleOption from Select
				filterOption={onFilterOption}
				placeholder={placeholder ?? <FormattedMessage {...messages.searchPlaceholder} />}
				// Typescript fix: normalized value shape (scalar vs array) is not assignable to Select's typed value
				value={normalizedValue as any}
			/>
		);
	},
);

LocalBaseSelect.displayName = 'LocalBaseSelect';
